import {DistinctDomainKeyWithChainIndex} from 'antibodies/AntibodyTypedefs';
import {ImplementedAntibodySchemaInterface} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import {
  AntibodyChainInput,
  AntibodyDomainInput,
  AntibodyDomainPairInput,
  AntibodyPairAwareCreateInput,
  DistinctDomainKeyDict,
  DistinctChainKeyDict,
  AntibodyPairAwareCreateInputDict,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

/**
 * Returns true if every constant domain has a DNA or AA component specified.
 */
export function areConstantDomainsFullyDefined(constantDomains: Array<AntibodyDomainInput>): boolean {
  return constantDomains.every((domain) => domain.dnaComponent != null || domain.aaComponent != null);
}

function buildSchemaIdToFormatMap(
  implementedInterfaces: Array<ImplementedAntibodySchemaInterface>
): Map<string, AntibodyFormatGraphQL> {
  return new Map(
    implementedInterfaces.map((implementedInterface) => [
      implementedInterface.schema.id,
      implementedInterface.format,
    ])
  );
}

function buildFormatIdToSchemaIdsMap(
  implementedInterfaces: Array<ImplementedAntibodySchemaInterface>
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const implementedInterface of implementedInterfaces) {
    const formatId = implementedInterface.format.id;
    if (!map.has(formatId)) {
      map.set(formatId, new Set());
    }
    map.get(formatId)!.add(implementedInterface.schema.id);
  }
  return map;
}

function buildAllSchemaIdsSet(implementedInterfaces: Array<ImplementedAntibodySchemaInterface>): Set<string> {
  return new Set(implementedInterfaces.map((implementedInterface) => implementedInterface.schema.id));
}

export interface AntibodySchemaFormatMaps {
  schemaIdToFormat: Map<string, AntibodyFormatGraphQL>;
  formatIdToSchemaIds: Map<string, Set<string>>;
  allSchemaIds: Set<string>;
}

/**
 * Builds maps and sets for navigating relationships between antibody schemas and formats from the implemented
 * antibody schema interfaces.
 */
export function buildAntibodySchemaFormatMaps(
  implementedAntibodySchemaInterfaces: Array<ImplementedAntibodySchemaInterface>
): AntibodySchemaFormatMaps {
  return {
    schemaIdToFormat: buildSchemaIdToFormatMap(implementedAntibodySchemaInterfaces),
    formatIdToSchemaIds: buildFormatIdToSchemaIdsMap(implementedAntibodySchemaInterfaces),
    allSchemaIds: buildAllSchemaIdsSet(implementedAntibodySchemaInterfaces),
  };
}

function _convertFrontendDistinctDomainKeyToBackendDistinctDomainKey(
  key: DistinctDomainKeyWithChainIndex
): DistinctDomainKeyDict {
  return {domainType: key.type, specificity: key.specificity};
}

function _prepareAntibodyDomainPairInput(pair: AntibodyDomainPairInput): Omit<
  AntibodyDomainPairInput,
  'pairKey'
> & {
  pairKey: {
    distinctDomainForPairVh: DistinctDomainKeyDict;
    distinctDomainForPairVl: DistinctDomainKeyDict;
  };
} {
  return {
    ...pair,
    pairKey: {
      distinctDomainForPairVh: _convertFrontendDistinctDomainKeyToBackendDistinctDomainKey(
        pair.pairKey.distinctDomainForPairVh
      ),
      distinctDomainForPairVl: _convertFrontendDistinctDomainKeyToBackendDistinctDomainKey(
        pair.pairKey.distinctDomainForPairVl
      ),
    },
  };
}

function _prepareAntibodyDomainInput(domain: AntibodyDomainInput): Omit<
  AntibodyDomainInput,
  'componentFormatKey'
> & {
  componentFormatKey: DistinctDomainKeyDict;
} {
  return {
    ...domain,
    componentFormatKey: _convertFrontendDistinctDomainKeyToBackendDistinctDomainKey(
      domain.componentFormatKey
    ),
  };
}

function _prepareAntibodyChainInput(chain: AntibodyChainInput): Omit<
  AntibodyDomainInput,
  'componentFormatKey'
> & {
  componentFormatKey: DistinctChainKeyDict;
} {
  return {
    ...chain,
    componentFormatKey: {label: chain.componentFormatKey.label},
  };
}

/**
 * Prepares the antibody create inputs for the backend, removing frontend-only fields, and converting frontend
 * variations of DistinctDomainKey and DistinctChainKey to the backend types.
 */
export function prepareAntibodyCreateInputs(
  input: AntibodyPairAwareCreateInput
): AntibodyPairAwareCreateInputDict {
  return {
    pairs: input.pairs?.map((pair) => _prepareAntibodyDomainPairInput(pair)) ?? null,
    domains: input.domains?.map((domain) => _prepareAntibodyDomainInput(domain)) ?? null,
    chains: input.chains?.map((chain) => _prepareAntibodyChainInput(chain)) ?? null,
  };
}
